import React, { useCallback, useState } from 'react';
import { useNavigation } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';
import { RootStackParamList } from '@/navigation/RootNavigator';
import {
  useDeleteThreadMutation,
  useReportContentMutation,
} from '@/store/api/corkBoard/corkBoardApi';
import { useCorkBoardMutation } from '@/hooks/residents/corkBoard/useCorkBoardMutation';
import { isFetchBaseQueryError } from '@/utils/admin/errorHandling';
import { showAlert } from '@/utils/common/alert';
import { CorkBoardReportModal } from '@/components/residents/corkBoard';
import { ConfirmationModal } from '@/components/residents/marketplace/ConfirmationModal';
import type { CorkBoardThreadSummary, ReportReason } from '@/types/residents/corkBoard';

type NavigationProp = StackNavigationProp<RootStackParamList>;

interface UseThreadActionsOptions {
  /** Fires after the thread has been successfully deleted on the server. */
  onDeleteSuccess?: (thread: CorkBoardThreadSummary) => void;
}

interface UseThreadActionsResult {
  handleEditThread: (thread: CorkBoardThreadSummary) => void;
  openDeleteConfirm: (thread: CorkBoardThreadSummary) => void;
  openReportModal: (thread: CorkBoardThreadSummary) => void;
  threadActionModals: React.ReactElement;
}

export function useThreadActions(
  options: UseThreadActionsOptions = {},
): UseThreadActionsResult {
  const { onDeleteSuccess } = options;
  const navigation = useNavigation<NavigationProp>();

  const [deleteThreadTrigger] = useDeleteThreadMutation();
  const [reportContentTrigger, { isLoading: isReporting }] = useReportContentMutation();

  const { execute: executeDeleteThread } = useCorkBoardMutation<string, void>(
    deleteThreadTrigger,
    { errorFallback: 'Failed to delete thread.' },
  );
  const { execute: executeReport } = useCorkBoardMutation<
    { threadId?: number; reason: ReportReason; details?: string },
    { id: string } | null
  >(
    reportContentTrigger,
    { errorFallback: 'Failed to submit report.' },
  );

  const [selectedThread, setSelectedThread] = useState<CorkBoardThreadSummary | null>(null);
  const [isDeleteConfirmVisible, setIsDeleteConfirmVisible] = useState(false);
  const [isReportModalVisible, setIsReportModalVisible] = useState(false);
  const [hasReported, setHasReported] = useState(false);

  const handleEditThread = useCallback((thread: CorkBoardThreadSummary) => {
    navigation.navigate('EditThread', { threadId: thread.id });
  }, [navigation]);

  const openDeleteConfirm = useCallback((thread: CorkBoardThreadSummary) => {
    setSelectedThread(thread);
    setIsDeleteConfirmVisible(true);
  }, []);

  const handleDeleteThread = useCallback(async () => {
    if (!selectedThread) return;
    setIsDeleteConfirmVisible(false);
    const threadToDelete = selectedThread;
    await executeDeleteThread(threadToDelete.id, {
      onSuccess: () => {
        onDeleteSuccess?.(threadToDelete);
      },
    });
  }, [selectedThread, executeDeleteThread, onDeleteSuccess]);

  const openReportModal = useCallback((thread: CorkBoardThreadSummary) => {
    setSelectedThread(thread);
    setHasReported(false);
    setIsReportModalVisible(true);
  }, []);

  const handleReportSubmit = useCallback(async (reason: ReportReason, details?: string) => {
    if (!selectedThread || isReporting) return;
    await executeReport(
      { threadId: Number(selectedThread.id), reason, details },
      {
        silent: true,
        onSuccess: () => {
          setIsReportModalVisible(false);
          setHasReported(true);
          showAlert('Report Submitted', 'Thank you. Our moderation team will review your report.');
        },
        onError: (_message, originalError) => {
          if (isFetchBaseQueryError(originalError) && originalError.status === 409) {
            setIsReportModalVisible(false);
            setHasReported(true);
            showAlert('Already Reported', 'You have already reported this content. Our team is reviewing it.');
          } else {
            showAlert('Error', 'Failed to submit report. Please try again.');
          }
        },
      },
    );
  }, [selectedThread, executeReport, isReporting]);

  const closeReportModal = useCallback(() => setIsReportModalVisible(false), []);
  const closeDeleteConfirm = useCallback(() => setIsDeleteConfirmVisible(false), []);

  const threadActionModals = (
    <>
      <CorkBoardReportModal
        visible={isReportModalVisible}
        onClose={closeReportModal}
        onSubmit={handleReportSubmit}
        isSubmitting={isReporting}
        hasAlreadyReported={hasReported}
        contentType="thread"
      />
      <ConfirmationModal
        visible={isDeleteConfirmVisible}
        message="Are you sure you want to delete this thread? This action cannot be undone."
        onCancel={closeDeleteConfirm}
        onProceed={handleDeleteThread}
        cancelText="Cancel"
        proceedText="Delete"
      />
    </>
  );

  return {
    handleEditThread,
    openDeleteConfirm,
    openReportModal,
    threadActionModals,
  };
}
